import React, { useMemo } from 'react';
import { Box, Chip, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { HelpOutline, ReportProblemOutlined } from '@mui/icons-material';
import { IArnoSelectorCatalog } from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  ANSWER_TYPE_LABEL,
  conditionSummary,
  ICondition,
  IEditorNode,
  NODE_KIND_LABEL,
} from './editorModel';
import { compareForPreview } from './conditionPreview';
import { FieldLabelResolver } from './fieldPathLabel';
import {
  IAlternative,
  NodePath,
  ReadBlock,
  ReadPiece,
  readingFlow,
} from './readingModel';
import { LIST_LABEL, migrationLabel } from './richTextModel';

/**
 * Ein Abschnitt, wie er sich liest (#1245, Teil 43).
 *
 * Text steht als Text da, nicht in Karten. Datenfelder und kurze Bedingungen stehen im Satz,
 * dezent markiert; Bedingungen mit eigenen Absätzen sind eingerückt und haben eine Kopfzeile.
 * Bearbeitet wird erst nach dem Anklicken: Text an Ort und Stelle (`renderEditor`), alles andere
 * im Detailbereich.
 */

export type ReaderSelection =
  | {
      kind: 'node';
      path: NodePath;
      /** Wo geklickt wurde: Absatz des Knotens und ungefähre Zeichenstelle darin. */
      paragraph?: number;
      offset?: number;
    }
  | { kind: 'field'; path: NodePath; selector: string; label?: string | null }
  | { kind: 'choice'; path: NodePath; alternatives: IAlternative[] };

export interface ReaderContext {
  questions: { questionId: string; prompt: string }[];
  fieldLabel: FieldLabelResolver;
  /** Wert des Feldes im Vorschauvorgang; `undefined` ohne Vorgang, `null` ohne Wert. */
  sample: (selector: string) => string | null | undefined;
  /**
   * Bearbeiten an Ort und Stelle (#1245, Teil 48): das Element an diesem Weg steht im Lesefluss
   * als Editor, gezeichnet von `renderEditor`.
   */
  editingPath?: NodePath | null;
  renderEditor?: (_node: IEditorNode, _path: NodePath) => React.ReactNode;
}

/** Wertvorschau aus dem Feldkatalog — nur der genaue Pfad: Beteiligter 2 ist nicht Beteiligter 1. */
export const sampleResolver =
  (catalog: IArnoSelectorCatalog | undefined) =>
  (selector: string): string | null | undefined => {
    if (!catalog?.idLegalTransaction) {
      return undefined;
    }
    for (const group of catalog.groups) {
      const field = group.fields.find(candidate => candidate.path === selector);
      if (field) {
        return field.sample;
      }
    }
    return null;
  };

/**
 * Trifft die Bedingung im Vorschauvorgang zu? Dieselbe vorsichtige Vorschau wie im
 * Bedingungsbaukasten (`compareForPreview`): wo sie es nicht sicher weiß, sagt sie nichts.
 */
export const previewVerdict = (
  condition: ICondition | null | undefined,
  context: ReaderContext
): boolean | null => {
  if (
    !condition ||
    condition.left.type !== 'selector' ||
    !condition.left.selector
  ) {
    return null;
  }
  const sample = context.sample(condition.left.selector);
  if (sample === undefined) {
    return null;
  }
  return compareForPreview(sample, condition.operator, condition.right);
};

const samePath = (a: NodePath | undefined, b: NodePath | undefined) =>
  Boolean(a && b) &&
  a!.length === b!.length &&
  a!.every((value, index) => value === b![index]);

interface Props {
  nodes: IEditorNode[];
  context: ReaderContext;
  selection: ReaderSelection | null;
  onSelect: (_selection: ReaderSelection) => void;
}

const hoverable = {
  cursor: 'pointer',
  borderRadius: 0.5,
  '&:hover': { outline: '1px solid', outlineColor: 'primary.light' },
};

const DEPTH_COLORS = ['#b0763a', '#3f7cac', '#6b8e23', '#8e5ea2', '#4a8f8f'];

export const SectionReader: React.FC<Props> = ({
  nodes,
  context,
  selection,
  onSelect,
}) => {
  const editingPath = context.editingPath ?? null;
  const blocks = useMemo(
    () => readingFlow(nodes, [], editingPath),
    [nodes, editingPath]
  );
  if (blocks.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Dieser Abschnitt ist noch leer.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1rem',
        lineHeight: 1.7,
        // Volle Breite der Karte — auch der Editor an Ort und Stelle (#1245, Teil 48). Wo eine
        // Seitenbreite gewollt ist (»In DiNo erstellen«), begrenzt der umgebende Rahmen.
      }}
      data-testid='section-reader'
    >
      <Blocks
        blocks={blocks}
        context={context}
        selection={selection}
        onSelect={onSelect}
        depth={0}
      />
    </Box>
  );
};

const Blocks: React.FC<{
  blocks: ReadBlock[];
  context: ReaderContext;
  selection: ReaderSelection | null;
  onSelect: (_selection: ReaderSelection) => void;
  depth: number;
}> = ({ blocks, context, selection, onSelect, depth }) => {
  // Nummerierte Listen zählen je zusammenhängender Folge — nur für die Anzeige.
  let counter = 0;
  return (
    <>
      {blocks.map((block, index) => {
        const key = `${block.path.join('.')}-${index}`;
        if (block.kind === 'paragraph') {
          const list = block.paragraph.list;
          counter =
            list === 'decimal' || list === 'lowerLetter' ? counter + 1 : 0;
          return (
            <Paragraph
              key={key}
              block={block}
              marker={list ? listMarker(list, counter) : null}
              context={context}
              selection={selection}
              onSelect={onSelect}
            />
          );
        }
        counter = 0;
        if (block.kind === 'editor') {
          return (
            <Box key={key} sx={{ my: 1 }}>
              {context.renderEditor?.(block.node, block.path)}
            </Box>
          );
        }
        if (block.kind === 'condition') {
          return (
            <ConditionBlock
              key={key}
              block={block}
              context={context}
              selection={selection}
              onSelect={onSelect}
              depth={depth}
            />
          );
        }
        if (block.kind === 'table') {
          return (
            <Box
              key={key}
              component='table'
              onClick={() => onSelect({ kind: 'node', path: block.path })}
              sx={{
                ...hoverable,
                borderCollapse: 'collapse',
                my: 1.5,
                minWidth: '60%',
              }}
            >
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <Box
                        component='td'
                        key={cellIndex}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          px: 1,
                          py: 0.5,
                          verticalAlign: 'top',
                        }}
                      >
                        <Blocks
                          blocks={cell}
                          context={context}
                          selection={selection}
                          onSelect={onSelect}
                          depth={depth}
                        />
                      </Box>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Box>
          );
        }
        return (
          <ElementLine
            key={key}
            node={block.node}
            selected={
              selection?.kind === 'node' && samePath(selection.path, block.path)
            }
            onClick={() => onSelect({ kind: 'node', path: block.path })}
            context={context}
          />
        );
      })}
    </>
  );
};

const listMarker = (list: keyof typeof LIST_LABEL, counter: number): string => {
  if (list === 'bullet') return '•';
  if (list === 'lowerLetter')
    return `${String.fromCharCode(96 + Math.min(counter, 26))})`;
  return `${counter}.`;
};

const Paragraph: React.FC<{
  block: Extract<ReadBlock, { kind: 'paragraph' }>;
  marker: string | null;
  context: ReaderContext;
  selection: ReaderSelection | null;
  onSelect: (_selection: ReaderSelection) => void;
}> = ({ block, marker, context, selection, onSelect }) => {
  const style = block.paragraph.style;
  const heading =
    style === 'heading1' || style === 'heading2' || style === 'heading3';
  const empty = block.pieces.length === 0;
  const selected =
    selection?.kind === 'node' && samePath(selection.path, block.path);
  return (
    <Box
      component='p'
      onClick={event =>
        onSelect({
          kind: 'node',
          path: block.path,
          paragraph: block.paragraphIndex,
          offset: clickOffset(event),
        })
      }
      sx={{
        ...hoverable,
        m: 0,
        mb: empty ? 0 : 1,
        minHeight: empty ? '0.8em' : undefined,
        pl: marker ? 3 : 0,
        position: 'relative',
        textAlign:
          block.paragraph.alignment === 'center'
            ? 'center'
            : block.paragraph.alignment === 'right'
              ? 'right'
              : block.paragraph.alignment === 'both'
                ? 'justify'
                : 'left',
        fontWeight: heading ? 700 : undefined,
        fontSize:
          style === 'heading1'
            ? '1.2rem'
            : style === 'heading2'
              ? '1.1rem'
              : undefined,
        bgcolor: selected ? 'action.selected' : undefined,
      }}
    >
      {block.paragraph.pageBreakBefore && (
        // Word beginnt hier eine neue Seite — im Lesefluss nur als dezente Linie.
        <Box
          component='span'
          sx={{
            display: 'block',
            borderTop: '1px dashed',
            borderColor: 'divider',
            color: 'text.disabled',
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            fontSize: '0.7rem',
            textAlign: 'center',
            mb: 0.5,
          }}
        >
          neue Seite
        </Box>
      )}
      {marker && (
        <Box
          component='span'
          sx={{ position: 'absolute', left: 0, color: 'text.secondary' }}
        >
          {marker}
        </Box>
      )}
      {block.pieces.map((piece, index) => (
        <Piece
          key={index}
          piece={piece}
          context={context}
          selection={selection}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
};

/**
 * Die Zeichenstelle eines Klicks im Absatz — ungefähr: Datenfelder zeigen in der Leseansicht
 * ihren Wert, im Editor zählen sie als ein Zeichen. Für »Cursor ungefähr dort« genügt das.
 */
const clickOffset = (
  event: React.MouseEvent<HTMLElement>
): number | undefined => {
  const caret = (
    document as Document & {
      caretRangeFromPoint?: (_x: number, _y: number) => Range | null;
    }
  ).caretRangeFromPoint?.(event.clientX, event.clientY);
  if (!caret || !event.currentTarget.contains(caret.startContainer)) {
    return undefined;
  }
  const before = document.createRange();
  before.selectNodeContents(event.currentTarget);
  before.setEnd(caret.startContainer, caret.startOffset);
  return before.toString().length;
};

const inlineChip = (
  tone: 'field' | 'choice' | 'open' | 'marker',
  active: boolean
): SxProps<Theme> => ({
  display: 'inline',
  px: 0.5,
  mx: 0.125,
  borderRadius: 0.5,
  cursor: 'pointer',
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  fontSize: '0.9em',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
  outline: active ? '2px solid' : undefined,
  outlineColor: 'primary.main',
  ...(tone === 'field' && {
    bgcolor: '#e8f1fb',
    borderBottom: '1px dotted #3f7cac',
  }),
  ...(tone === 'choice' && {
    bgcolor: '#fdf1e2',
    borderBottom: '1px dotted #b0763a',
  }),
  ...(tone === 'open' && {
    color: 'error.main',
    border: '1px dashed',
    borderColor: 'error.main',
  }),
  ...(tone === 'marker' && { bgcolor: 'action.hover' }),
});

const Piece: React.FC<{
  piece: ReadPiece;
  context: ReaderContext;
  selection: ReaderSelection | null;
  onSelect: (_selection: ReaderSelection) => void;
}> = ({ piece, context, selection, onSelect }) => {
  const stop = (event: React.MouseEvent) => event.stopPropagation();
  switch (piece.kind) {
    case 'text':
      return (
        <Box
          component='span'
          sx={{
            fontWeight: piece.bold ? 700 : undefined,
            fontStyle: piece.italic ? 'italic' : undefined,
            textDecoration: piece.underline ? 'underline' : undefined,
            whiteSpace: 'pre-wrap',
          }}
        >
          {piece.text}
        </Box>
      );
    case 'break':
      return <br />;
    case 'field': {
      const name =
        context.fieldLabel(piece.selector) ?? piece.label ?? 'Datenfeld';
      const value = context.sample(piece.selector);
      const active =
        selection?.kind === 'field' &&
        samePath(selection.path, piece.path) &&
        selection.selector === piece.selector;
      return (
        <Tooltip title={`Datenfeld: ${name}`}>
          <Box
            component='span'
            sx={inlineChip('field', active)}
            onClick={event => {
              stop(event);
              onSelect({
                kind: 'field',
                path: piece.path,
                selector: piece.selector,
                label: piece.label,
              });
            }}
          >
            {value ? value : `‹${name}›`}
          </Box>
        </Tooltip>
      );
    }
    case 'choice': {
      const verdicts = piece.alternatives.map(alternative =>
        previewVerdict(alternative.condition, context)
      );
      const active =
        selection?.kind === 'choice' && samePath(selection.path, piece.path);
      return (
        <Tooltip title='Bedingter Text — anklicken für die Bedingung'>
          <Box
            component='span'
            sx={inlineChip('choice', active)}
            onClick={event => {
              stop(event);
              onSelect({
                kind: 'choice',
                path: piece.path,
                alternatives: piece.alternatives,
              });
            }}
          >
            {piece.alternatives.map((alternative, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Box
                    component='span'
                    sx={{ color: 'text.disabled', mx: 0.25 }}
                  >
                    |
                  </Box>
                )}
                <Box
                  component='span'
                  sx={{
                    color:
                      verdicts[index] === false ? 'text.disabled' : undefined,
                    fontWeight: verdicts[index] === true ? 700 : undefined,
                  }}
                >
                  {alternative.text.trim() || '(nichts)'}
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Tooltip>
      );
    }
    case 'open':
      return (
        <Tooltip title='Diese Stelle konnte beim Import nicht sicher übernommen werden.'>
          <Box
            component='span'
            sx={inlineChip('open', false)}
            onClick={event => {
              stop(event);
              onSelect({ kind: 'node', path: piece.path });
            }}
          >
            {migrationLabel(piece.mark)}
          </Box>
        </Tooltip>
      );
    case 'marker':
      return (
        <Box
          component='span'
          sx={inlineChip(
            piece.node.type === 'migration_required' ? 'open' : 'marker',
            false
          )}
          onClick={event => {
            stop(event);
            onSelect({ kind: 'node', path: piece.path });
          }}
        >
          {piece.node.type === 'question'
            ? `Frage: ${piece.node.prompt ?? ''}`
            : piece.node.type === 'migration_required'
              ? 'offene Stelle'
              : NODE_KIND_LABEL[piece.node.type]}
        </Box>
      );
    default:
      return null;
  }
};

const VerdictChip: React.FC<{ verdict: boolean | null }> = ({ verdict }) =>
  verdict === null ? null : (
    <Chip
      size='small'
      label={
        verdict
          ? 'trifft im Vorschauvorgang zu'
          : 'trifft im Vorschauvorgang nicht zu'
      }
      color={verdict ? 'success' : 'default'}
      variant='outlined'
      sx={{
        ml: 1,
        height: 20,
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      }}
    />
  );

const ConditionBlock: React.FC<{
  block: Extract<ReadBlock, { kind: 'condition' }>;
  context: ReaderContext;
  selection: ReaderSelection | null;
  onSelect: (_selection: ReaderSelection) => void;
  depth: number;
}> = ({ block, context, selection, onSelect, depth }) => {
  const color = DEPTH_COLORS[depth % DEPTH_COLORS.length];
  const verdict = previewVerdict(block.condition, context);
  const selected =
    selection?.kind === 'node' && samePath(selection.path, block.path);
  return (
    <Box
      sx={{
        borderLeft: `2px solid ${color}`,
        pl: 1.5,
        my: 1,
        opacity: verdict === false ? 0.6 : 1,
      }}
    >
      <Box
        onClick={() => onSelect({ kind: 'node', path: block.path })}
        sx={{
          ...hoverable,
          display: 'inline-flex',
          alignItems: 'center',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontSize: '0.8rem',
          color,
          fontWeight: 500,
          mb: 0.5,
          bgcolor: selected ? 'action.selected' : undefined,
        }}
      >
        {conditionSummary(
          block.condition,
          context.questions,
          context.fieldLabel
        ) || 'Bedingung ohne Regel'}
        <VerdictChip verdict={verdict} />
      </Box>
      <Blocks
        blocks={block.blocks}
        context={context}
        selection={selection}
        onSelect={onSelect}
        depth={depth + 1}
      />
    </Box>
  );
};

/** Frage, Verweis, Hinweis, Ende, offene Stelle — was im Ablauf steht, aber kein Text ist. */
export const elementText = (
  node: IEditorNode,
  context: ReaderContext
): string => {
  switch (node.type) {
    case 'question': {
      const answers =
        node.answerType === 'SINGLE_CHOICE' && node.answerOptions?.length
          ? node.answerOptions.join(' / ')
          : node.answerType === 'BOOLEAN'
            ? 'Ja / Nein'
            : ANSWER_TYPE_LABEL[node.answerType ?? 'TEXT'];
      return `Frage: ${node.prompt ?? ''} — Antwort: ${answers}${node.binding?.selector ? ' · wird aus dem Vorgang beantwortet' : ''}`;
    }
    case 'choice':
      return `Auswahl: ${(node.options ?? []).map(option => option.label).join(' / ')}`;
    case 'input':
      return `Eingabe: ${node.prompt ?? node.label ?? ''}`;
    case 'info':
      return `Hinweis: ${node.text ?? ''}`;
    case 'stop':
      return 'Hier endet die Urkunde.';
    case 'block_reference':
      return `Textbaustein: ${node.reference?.blockName ?? 'nicht gewählt'}`;
    case 'conditional_block_reference':
      return `${conditionSummary(node.condition, context.questions, context.fieldLabel)} → Textbaustein: ${node.reference?.blockName ?? 'nicht gewählt'}`;
    case 'migration_required':
      return 'Diese Stelle konnte beim Import nicht sicher übernommen werden.';
    case 'data_field':
      return `Datenfeld: ${context.fieldLabel(node.selector ?? '') ?? node.label ?? ''}`;
    default:
      return NODE_KIND_LABEL[node.type] ?? node.type;
  }
};

const ElementLine: React.FC<{
  node: IEditorNode;
  selected: boolean;
  onClick: () => void;
  context: ReaderContext;
}> = ({ node, selected, onClick, context }) => {
  const problem = node.type === 'migration_required';
  return (
    <Box
      onClick={onClick}
      sx={{
        ...hoverable,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        my: 1,
        px: 1,
        py: 0.5,
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        fontSize: '0.85rem',
        color: problem ? 'error.main' : 'text.secondary',
        bgcolor: selected
          ? 'action.selected'
          : problem
            ? '#fdeeee'
            : 'action.hover',
      }}
    >
      {problem ? (
        <ReportProblemOutlined fontSize='small' />
      ) : node.type === 'question' ? (
        <HelpOutline fontSize='small' />
      ) : null}
      <span>{elementText(node, context)}</span>
    </Box>
  );
};

/** Für die Detailansicht: der Zweigtext einer Alternative, lesbar. */
export const alternativeLine = (
  alternative: IAlternative,
  context: ReaderContext
): string =>
  `${conditionSummary(alternative.condition, context.questions, context.fieldLabel) || 'Ohne Bedingung'} → „${alternative.text.trim()}"`;
